/**
 * Every app-global keyboard shortcut, in one table.
 *
 * A chord is only useful if the page actually receives it. On macOS the
 * browser's own menu bar takes its key equivalents first — ⌘O opens a file
 * picker, ⌘E is "Use Selection for Find", ⌘, opens browser settings — and a
 * `keydown` listener never runs, so `preventDefault` cannot win a key the page
 * was never handed. Windows browsers claim the same letters for their own
 * menus (Ctrl+O, Ctrl+E). `UNAVAILABLE_CHORDS` records those, plus the chords
 * other parts of the app already own, and `assertHotkeyRegistry` fails the
 * build's unit tests if a shortcut lands on one or if two shortcuts collide.
 *
 * The app-global family is therefore `mod+alt+<key>` (⌥⌘ on macOS, Ctrl+Alt
 * elsewhere) — the same gesture the session view already uses for its
 * ⌘/Ctrl+Alt+digit tab switching. No browser binds ⌥⌘+letter except the
 * devtools trio, which is listed below. `⌘K` stays as it is: it is the one
 * single-modifier chord no browser menu claims, and it is the cross-app
 * convention for a command palette.
 */

/** A chord parsed into the flags a `KeyboardEvent` is matched against. */
export interface ParsedChord {
  /** ⌘ on Apple platforms, Ctrl elsewhere. Matched as "either one". */
  mod: boolean;
  alt: boolean;
  shift: boolean;
  /** The printable key, lowercased: `o`, `,`. */
  key: string;
  /** The US-layout `KeyboardEvent.code` the key sits on, when known. */
  code: string | undefined;
}

export interface HotkeyDefinition {
  /** The chord the app teaches and listens for. */
  chord: string;
  /** What the shortcut does, as the hotkeys listing and ⌘K rows say it. */
  label: string;
  /**
   * Chords kept alive for muscle memory. They are allowed to be
   * browser-claimed: where the browser eats one the binding is simply dead,
   * which is why they are never rendered as a hint.
   */
  legacyChords?: readonly string[];
  /**
   * When set, the hotkeys dialog lists this under that surface group instead
   * of "Anywhere in the app". Use for chords that are only bound while a
   * specific page is mounted (e.g. the agent session view).
   */
  listingGroup?: 'Session view';
}

export const HOTKEYS = {
  commandPalette: {
    chord: 'mod+k',
    label: 'Command palette',
  },
  jumpToSession: {
    // Was ⌘O, which macOS hands to the browser's File > Open File… menu item
    // before the page sees it. No legacy binding: Ctrl+O opens the same file
    // picker on Windows, so there is no platform where it would have worked.
    chord: 'mod+alt+o',
    label: 'Jump to session',
  },
  recentSessions: {
    // Was ⌘E — macOS Edit > Find > Use Selection for Find in Chrome and
    // Safari, and the address-bar search on Windows.
    chord: 'mod+alt+e',
    label: 'Recent sessions',
  },
  openSettings: {
    // Was ⌘, — every desktop browser's own Settings/Preferences key
    // equivalent. Kept as a legacy chord because the browsers that do pass it
    // through (Linux builds without a menu bar binding) cost nothing to keep
    // working, but the taught chord is the one that works everywhere.
    chord: 'mod+alt+,',
    legacyChords: ['mod+,'],
    label: 'Settings',
  },
  viewHotkeys: {
    chord: 'mod+alt+/',
    // Named for the ⌘K row that opens it, and distinct from the dialog's own
    // "Keyboard shortcuts" title so the listing does not repeat itself.
    label: 'View hotkeys',
  },
  launchAgain: {
    // Bound only while a live agent session is open (see AgentStreamView).
    // Kept in this table so the chord, hint, and conflict check stay one
    // source of truth with the rest of the ⌥⌘ family; listingGroup keeps the
    // hotkeys dialog honest that it is not app-global.
    chord: 'mod+alt+l',
    label: 'Launch again',
    listingGroup: 'Session view',
  },
} as const satisfies Record<string, HotkeyDefinition>;

export type HotkeyId = keyof typeof HOTKEYS;

/**
 * Chords an app-global shortcut may not take, and why. Two sources: the
 * browser/OS claims it before the page (so the shortcut would silently do
 * nothing), or another surface in this app already owns it (so the two would
 * both fire).
 */
const UNAVAILABLE_CHORDS: ReadonlyMap<string, string> = new Map([
  // Browser menu key equivalents. macOS routes these through the menu bar, and
  // Windows/Linux browsers bind the same letters.
  ['mod+n', 'browser: new window'],
  ['mod+shift+n', 'browser: new incognito/private window'],
  ['mod+t', 'browser: new tab'],
  ['mod+shift+t', 'browser: reopen closed tab'],
  ['mod+w', 'browser: close tab'],
  ['mod+q', 'browser: quit'],
  ['mod+o', 'browser: open file'],
  ['mod+e', 'browser: use selection for find (macOS) / address bar (Windows)'],
  ['mod+,', 'browser: settings/preferences'],
  ['mod+l', 'browser: focus the address bar'],
  ['mod+d', 'browser: bookmark this page'],
  ['mod+f', 'browser: find in page'],
  ['mod+g', 'browser: find next'],
  ['mod+y', 'browser: history'],
  ['mod+r', 'browser: reload'],
  ['mod+m', 'browser: minimize (macOS)'],
  ['mod+h', 'browser: hide (macOS)'],
  // The devtools corner of the ⌥⌘ family the app-global chords live in.
  ['mod+alt+i', 'browser: devtools'],
  ['mod+alt+j', 'browser: devtools console'],
  ['mod+alt+c', 'browser: inspect element'],
  ['mod+alt+u', 'browser: view source'],
  // Owned elsewhere in the app: the session view's tab rail and the shared
  // `SegmentedTabs` strip switch tabs with ⌘/Ctrl+Alt+digit.
  ...(['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const).map(
    (digit) => [`mod+alt+${digit}`, 'app: VerticalTabRail / SegmentedTabs tab switching'] as const,
  ),
]);

/** `KeyboardEvent.code` for the non-alphanumeric keys a chord may name. */
const PUNCTUATION_CODES: Readonly<Record<string, string>> = {
  ',': 'Comma',
  '.': 'Period',
  '/': 'Slash',
  ';': 'Semicolon',
  "'": 'Quote',
  '[': 'BracketLeft',
  ']': 'BracketRight',
  '\\': 'Backslash',
  '-': 'Minus',
  '=': 'Equal',
  '`': 'Backquote',
};

function codeForKey(key: string): string | undefined {
  if (/^[a-z]$/.test(key)) return `Key${key.toUpperCase()}`;
  if (/^[0-9]$/.test(key)) return `Digit${key}`;
  return PUNCTUATION_CODES[key];
}

/**
 * Splits `mod+alt+o` into the flags an event is matched against. Modifiers may
 * appear in any order; the last token is the key.
 */
export function parseChord(chord: string): ParsedChord {
  const tokens = chord.toLowerCase().split('+');
  const key = tokens.at(-1) ?? '';
  const modifiers = new Set(tokens.slice(0, -1));
  return {
    mod: modifiers.has('mod'),
    alt: modifiers.has('alt'),
    shift: modifiers.has('shift'),
    key,
    code: codeForKey(key),
  };
}

/** Normalizes a chord so `alt+mod+o` and `mod+alt+o` compare equal. */
function normalizeChord(chord: string): string {
  const { mod, alt, shift, key } = parseChord(chord);
  return [mod && 'mod', alt && 'alt', shift && 'shift', key].filter(Boolean).join('+');
}

/**
 * Whether a `keydown` is this chord.
 *
 * The key is matched by `code` as well as `key` because ⌥ is a compose
 * modifier on macOS: ⌥⌘O arrives as `key: 'ø'` and ⌥⌘, as `key: '≤'`, so a
 * `key`-only comparison would miss every Option chord on a Mac. Matching
 * either way also keeps the mnemonic letter working on layouts where it sits
 * somewhere other than the US position.
 */
export function chordMatchesEvent(chord: ParsedChord, event: KeyboardEvent): boolean {
  if ((event.metaKey || event.ctrlKey) !== chord.mod) return false;
  if (event.altKey !== chord.alt) return false;
  if (event.shiftKey !== chord.shift) return false;
  return (
    event.key.toLowerCase() === chord.key || (chord.code !== undefined && event.code === chord.code)
  );
}

/** Every chord a hotkey listens for: the taught one first, then any legacy. */
function definitionChords(definition: HotkeyDefinition): string[] {
  return [definition.chord, ...(definition.legacyChords ?? [])];
}

/**
 * Parsed once at module load, not per keystroke: `matchesHotkey` runs on every
 * key the user types anywhere in the app.
 */
const PARSED_CHORDS: Readonly<Record<HotkeyId, ParsedChord[]>> = Object.fromEntries(
  Object.entries(HOTKEYS).map(([id, definition]) => [
    id,
    definitionChords(definition).map(parseChord),
  ]),
) as Record<HotkeyId, ParsedChord[]>;

/** Whether a `keydown` triggers this hotkey, by any of its chords. */
export function matchesHotkey(id: HotkeyId, event: KeyboardEvent): boolean {
  return PARSED_CHORDS[id].some((chord) => chordMatchesEvent(chord, event));
}

/**
 * The taught chord as `KbdCombo` keys, e.g. `['mod', 'alt', 'o']`. Built from
 * the parsed chord so the hint reads in a fixed modifier order however the
 * table spells it. `mod` renders as ⌘ on Apple platforms and Ctrl elsewhere.
 */
export function hotkeyKeys(id: HotkeyId): string[] {
  return normalizeChord(HOTKEYS[id].chord).split('+');
}

export interface ShortcutReferenceItem {
  /** `KbdCombo` keys, e.g. `['mod', 'alt', 'o']`. */
  keys: string[];
  label: string;
}

export interface ShortcutReferenceGroup {
  title: string;
  items: ShortcutReferenceItem[];
}

/**
 * Shortcuts owned by a component rather than by this table, listed so the
 * hotkeys dialog is the whole keyboard surface and not just the global half.
 * They are display-only: the component still binds them, and it stays the
 * place to change them. Keep an entry honest — a listing that lies is worse
 * than no listing — and prefer moving a shortcut into `HOTKEYS` over adding
 * to this list when it really is app-global.
 */
const SURFACE_SHORTCUTS: readonly ShortcutReferenceGroup[] = [
  {
    title: 'Session view',
    items: [
      // `VerticalTabRail` / `SegmentedTabs`, tracking the visible strip order.
      { keys: ['mod', 'alt', '1'], label: 'Switch to view tab 1…9' },
      // `useStepHotkeys` in plugin-chat, when focus is not in a field.
      { keys: ['alt', 'up'], label: 'Previous turn' },
      { keys: ['alt', 'down'], label: 'Next turn' },
    ],
  },
  {
    title: 'Palettes and takeovers',
    items: [
      { keys: ['up'], label: 'Previous row' },
      { keys: ['down'], label: 'Next row' },
      { keys: ['enter'], label: 'Open the selected row' },
      { keys: ['esc'], label: 'Close, or leave a takeover surface' },
    ],
  },
];

/**
 * Every shortcut worth showing a user, grouped for the hotkeys dialog. The
 * global group is generated from `HOTKEYS` entries without a `listingGroup`;
 * entries that name a surface are prepended onto that surface's
 * `SURFACE_SHORTCUTS` group so a session-scoped binding is not advertised as
 * working everywhere.
 */
export function shortcutReference(): ShortcutReferenceGroup[] {
  const globalItems: ShortcutReferenceItem[] = [];
  const bySurface = new Map<string, ShortcutReferenceItem[]>();
  for (const [id, definition] of Object.entries(HOTKEYS) as [HotkeyId, HotkeyDefinition][]) {
    const item = { keys: hotkeyKeys(id), label: definition.label };
    if (definition.listingGroup === undefined) {
      globalItems.push(item);
      continue;
    }
    const items = bySurface.get(definition.listingGroup) ?? [];
    items.push(item);
    bySurface.set(definition.listingGroup, items);
  }
  return [
    { title: 'Anywhere in the app', items: globalItems },
    ...SURFACE_SHORTCUTS.map((group) => ({
      title: group.title,
      items: [...(bySurface.get(group.title) ?? []), ...group.items],
    })),
  ];
}

/**
 * Fails when the table has a shortcut that a browser or another surface would
 * swallow, or two shortcuts on the same chord. Called from the unit tests, so
 * a new shortcut on a dead or duplicated chord cannot reach a review.
 */
export function assertHotkeyRegistry(
  table: Readonly<Record<string, HotkeyDefinition>> = HOTKEYS,
): void {
  const seen = new Map<string, string>();
  for (const [id, definition] of Object.entries(table)) {
    // Only the taught chord has to be one the page receives; a legacy chord is
    // allowed to be browser-claimed (that is usually why it is legacy). Both
    // kinds are listened for, so both take part in the collision check.
    const unavailable = UNAVAILABLE_CHORDS.get(normalizeChord(definition.chord));
    if (unavailable) {
      throw new Error(
        `Hotkey "${id}" uses ${definition.chord}, which is already taken: ${unavailable}.`,
      );
    }
    for (const chord of definitionChords(definition).map(normalizeChord)) {
      const owner = seen.get(chord);
      if (owner) {
        throw new Error(`Hotkeys "${owner}" and "${id}" both use ${chord}.`);
      }
      seen.set(chord, id);
    }
  }
}
